import { agreementSigned, agreementVersionLabel, AGREEMENT_DOCUMENT_VERSION, missingAgreementRequirements, partySigned, withRemoteClientSignature, type V2Agreement } from '../domain/agreement';
import { displayAddress, type V2Inspection } from '../domain/inspection';
import { inspectionServiceOf } from '../domain/inspectionService';
import type { SignatureStrokes } from '../domain/signature';
import { translate } from '../i18n/i18n';
import type { AppLocale } from '../i18n/locales';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_REMOTE_SIGN_KEY } from '../persistence/persistenceContract';
import { renderAgreementDocumentHtml } from '../report/agreementHtml';
import { remoteSignUrl, type RemoteSignFacts } from '../../supabase/functions/_shared/remoteSignProtocol';
import { currentAccessToken } from '../backend/aiClient';
import { getRemoteSignTransport, remoteSignBase } from '../backend/remoteSignClient';
import { createStore } from './createStore';
import { inspectionStore, updateAgreementAction } from './inspectionStore';

// Remote signing of the agreement (Owner decision list 2026-10-19 items 4 / 45), on the inspector's side.
//   1. create   the document as the client will read it (the same document the PDF prints, rendered
//               here) goes to the server with the five facts and a fingerprint; the link comes back
//   2. check    when the client has signed, their part is taken back into the agreement — only if the
//               fingerprint still matches (fee, services, Demo, property, date, names, copy and service
//               unchanged since the link went out) — and the server's copy is deleted at once
//   3. the inspector signs on this device as usual (AgreementSignSheet), which completes the agreement.
// One open link per inspection; this account's open links are kept on the device (per account).
export interface RemoteSignLink {
  inspectionId: string;
  requestId: string;
  url: string;
  createdAt: number;
  expiresAt: number;
  fingerprint: string;
}

export type RemoteCreateResult = { kind: 'created'; link: RemoteSignLink } | { kind: 'failed'; reason: 'notReady' | 'noWebAddress' | 'offline' | 'notSignedIn' | 'unavailable' };
export type RemoteCheckResult = { kind: 'waiting' } | { kind: 'taken' } | { kind: 'expired' } | { kind: 'changed' } | { kind: 'gone' } | { kind: 'failed'; reason: 'offline' | 'unavailable' };

export const remoteSignStore = createStore<{ links: RemoteSignLink[] }>({ links: [] });

function readLinks(): RemoteSignLink[] {
  const key = scopedStorageKey(V2_REMOTE_SIGN_KEY);
  if (!key) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((l): l is RemoteSignLink => typeof l === 'object' && l !== null && typeof (l as RemoteSignLink).inspectionId === 'string' && typeof (l as RemoteSignLink).requestId === 'string' && typeof (l as RemoteSignLink).url === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeLinks(links: RemoteSignLink[]): void {
  remoteSignStore.set(() => ({ links }));
  const key = scopedStorageKey(V2_REMOTE_SIGN_KEY);
  if (!key) return;
  try {
    if (links.length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(links));
  } catch {
    // storage full / private window: the link still works for this session
  }
}

// Called when an account's data is opened (and in tests).
export function hydrateRemoteSign(): void {
  remoteSignStore.set(() => ({ links: readLinks() }));
}

export function remoteSignLinkFor(inspectionId: string): RemoteSignLink | null {
  return remoteSignStore.get().links.find((l) => l.inspectionId === inspectionId) ?? null;
}

// Owner 2026-10-20: an open link travels with an inspection that a file carries to this device, so the
// client's signature can still be taken in here. Validated like a stored one.
export function adoptRemoteSignLink(raw: unknown): void {
  const link = raw as Partial<RemoteSignLink> | null;
  if (!link || typeof link.inspectionId !== 'string' || typeof link.requestId !== 'string' || typeof link.url !== 'string' || typeof link.fingerprint !== 'string') return;
  const adopted: RemoteSignLink = {
    inspectionId: link.inspectionId,
    requestId: link.requestId,
    url: link.url,
    createdAt: typeof link.createdAt === 'number' ? link.createdAt : 0,
    expiresAt: typeof link.expiresAt === 'number' ? link.expiresAt : 0,
    fingerprint: link.fingerprint
  };
  writeLinks([...remoteSignStore.get().links.filter((l) => l.inspectionId !== adopted.inspectionId), adopted]);
}

// Everything the client's signature covers. If any of it changes after the link went out, the signature that
// comes back is refused (the client signed a different document).
export function agreementFingerprint(inspection: Pick<V2Inspection, 'property' | 'client' | 'inspector' | 'inspectionAt' | 'agreement'>): string {
  const a = inspection.agreement;
  const text = JSON.stringify([
    a.fee.amount.trim(),
    a.demo,
    a.additionalServices.map((s) => [s.name.trim(), s.fee.trim()]),
    displayAddress(inspection, ''),
    inspection.inspectionAt,
    inspection.client.name.trim(),
    inspection.inspector.name.trim(),
    AGREEMENT_DOCUMENT_VERSION,
    inspectionServiceOf(inspection.property),
    // v5: the client read and signed the document in this language.
    a.documentLanguage
  ]);
  // FNV-1a, 32 bit, twice with different seeds: a change detector, not a secret.
  const hash = (seed: number) => {
    let h = seed >>> 0;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return `${hash(0x811c9dc5)}${hash(0x01234567)}`;
}

// May a link be sent now? The same things the in-person signing needs before anyone signs: a fee or Demo, and
// complete additional services. (Names: the client types their own on the page.)
export function remoteSignReady(agreement: V2Agreement): boolean {
  const missing = missingAgreementRequirements(agreement);
  return !agreementSigned(agreement) && !missing.includes('fee') && !missing.includes('additionalServices');
}

function factsOf(inspection: V2Inspection, locale: AppLocale, fingerprint: string): RemoteSignFacts {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const a = inspection.agreement;
  return {
    property: displayAddress(inspection, t('inspection.noAddress')),
    client: inspection.client.name.trim() || a.client.name.trim(),
    inspector: inspection.inspector.name.trim() || a.inspector.name.trim(),
    inspectionDate: new Date(inspection.inspectionAt).toLocaleString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
    fee: a.demo ? t('agreement.feeDemo') : `$${a.fee.amount.trim()}`,
    textVersion: agreementVersionLabel(AGREEMENT_DOCUMENT_VERSION),
    fingerprint
  };
}

// `_appLocale` is the inspector's App Language; since v5 the link follows the agreement's own language instead.
export async function createRemoteSignLinkAction(inspectionId: string, _appLocale: AppLocale, now: number = Date.now()): Promise<RemoteCreateResult> {
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!inspection || !remoteSignReady(inspection.agreement)) return { kind: 'failed', reason: 'notReady' };
  const base = remoteSignBase();
  if (!base) return { kind: 'failed', reason: 'noWebAddress' };
  // The document exactly as the client will read it: unsigned, the client's name as recorded.
  const unsignedDoc = { ...inspection, agreement: { ...inspection.agreement, client: { ...inspection.agreement.client, signature: null, accepted: false, signedAt: null }, inspector: { ...inspection.agreement.inspector, signature: null, accepted: false, signedAt: null } } };
  const documentHtml = renderAgreementDocumentHtml(unsignedDoc, now);
  const fingerprint = agreementFingerprint(inspection);
  // v5: the facts and the link speak the agreement's language, the one chosen for this client.
  const documentLocale = inspection.agreement.documentLanguage;
  const outcome = await getRemoteSignTransport().call({ action: 'create', inspectionId, documentHtml, facts: factsOf(inspection, documentLocale, fingerprint), locale: documentLocale }, await currentAccessToken());
  if (outcome.kind === 'failed') return { kind: 'failed', reason: outcome.code === 'offline' ? 'offline' : outcome.code === 'unauthenticated' ? 'notSignedIn' : 'unavailable' };
  if (outcome.response.action !== 'create') return { kind: 'failed', reason: 'unavailable' };
  const link: RemoteSignLink = { inspectionId, requestId: outcome.response.requestId, url: remoteSignUrl(base, outcome.response.token), createdAt: now, expiresAt: outcome.response.expiresAt, fingerprint };
  writeLinks([...remoteSignStore.get().links.filter((l) => l.inspectionId !== inspectionId), link]);
  return { kind: 'created', link };
}

function forget(inspectionId: string): void {
  writeLinks(remoteSignStore.get().links.filter((l) => l.inspectionId !== inspectionId));
}

export async function checkRemoteSignAction(inspectionId: string): Promise<RemoteCheckResult> {
  const link = remoteSignLinkFor(inspectionId);
  if (!link) return { kind: 'gone' };
  const outcome = await getRemoteSignTransport().call({ action: 'status', requestId: link.requestId }, await currentAccessToken());
  if (outcome.kind === 'failed') {
    if (outcome.code === 'notFound') {
      forget(inspectionId);
      return { kind: 'gone' };
    }
    return { kind: 'failed', reason: outcome.code === 'offline' ? 'offline' : 'unavailable' };
  }
  if (outcome.response.action !== 'status') return { kind: 'failed', reason: 'unavailable' };
  if (outcome.response.state === 'waiting') return { kind: 'waiting' };
  if (outcome.response.state === 'expired') return { kind: 'expired' };
  const result = outcome.response.result;
  const inspection = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!result || !inspection) return { kind: 'gone' };
  // The client signed THIS document only if nothing it covers changed since the link went out.
  if (agreementFingerprint(inspection) !== link.fingerprint || agreementSigned(inspection.agreement) || partySigned(inspection.agreement.inspector)) return { kind: 'changed' };
  const next = withRemoteClientSignature(inspection.agreement, {
    name: result.name,
    signature: result.signature as SignatureStrokes,
    esignConsentAt: result.esignConsentAt,
    aiConsent: result.aiConsent,
    aiConsentAt: result.aiConsentAt,
    liabilityAckAt: result.liabilityAckAt ?? null,
    receivedAt: result.signedAt,
    ip: result.ip,
    userAgent: result.userAgent
  });
  await updateAgreementAction(inspectionId, next);
  // Taken: the server's copy goes now (process data, Owner item 1); a failed delete expires on its own.
  await getRemoteSignTransport()
    .call({ action: 'discard', requestId: link.requestId }, await currentAccessToken())
    .catch(() => undefined);
  forget(inspectionId);
  return { kind: 'taken' };
}

export async function discardRemoteSignAction(inspectionId: string): Promise<void> {
  const link = remoteSignLinkFor(inspectionId);
  if (!link) return;
  forget(inspectionId);
  await getRemoteSignTransport()
    .call({ action: 'discard', requestId: link.requestId }, await currentAccessToken())
    .catch(() => undefined);
}

export function resetRemoteSignForTests(): void {
  remoteSignStore.set(() => ({ links: [] }));
}
